import { X, Zap, Crown, Target, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * SmartKick Predictions promo modal — the *only* monetisation surface
 * in the Scores app. Triggered by the funnel banner above the bottom
 * nav, by the header "AI Predictions" button, and once per day on a
 * 30-second timer for engaged sessions.
 *
 * On WEB we deliberately send users to the marketing landing site
 * (smartkick.co.uk) so the SEO/marketing pages do their job. On
 * NATIVE iOS we route to the in-app /free-trial and /free-preview
 * React Router pages so users never leave the app — Apple-safe and
 * Stripe-friendly (in-app browser via Capacitor's webview).
 */
const PREDICTIONS_URL = 'https://smartkick.co.uk/free-trial';
const PREDICTIONS_FREE_PREVIEW_URL = 'https://smartkick.co.uk/free-preview';

const isNativePlatform = () =>
  typeof window !== 'undefined'
  && window.Capacitor
  && typeof window.Capacitor.isNativePlatform === 'function'
  && window.Capacitor.isNativePlatform();

const features = [
  'Daily AI predictions with confidence scores',
  'SmartKick Daily 10 — top 10 settled picks',
  'VIP Bet Builders + Pick of the Day',
  '100-point bankroll performance tracker',
];

const PredictionsPromoModal = ({ open, onClose }) => {
  if (!open) return null;

  const native = isNativePlatform();
  // On native iOS, digital subscriptions MUST go through Apple IAP and the
  // buyer must NOT be forced to register first (App Store 3.1.1 + 5.1.1(v)).
  // Route straight to the in-app IAP subscription screen, where an anonymous
  // Apple purchase provisions the account automatically. The old /free-trial
  // path (Stripe + forced name/email/password) is web-only.
  const trialProps = native
    ? { as: Link, to: '/app/subscription', onClick: onClose }
    : { as: 'a', href: PREDICTIONS_URL, target: '_blank', rel: 'noopener noreferrer' };
  const previewProps = native
    ? { as: Link, to: '/free-preview', onClick: onClose }
    : { as: 'a', href: PREDICTIONS_FREE_PREVIEW_URL, target: '_blank', rel: 'noopener noreferrer' };
  const TrialTag = trialProps.as;
  const PreviewTag = previewProps.as;
  const trialAttrs = { ...trialProps }; delete trialAttrs.as;
  const previewAttrs = { ...previewProps }; delete previewAttrs.as;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="predictions-promo-modal"
    >
      <div
        className="w-full max-w-md bg-gradient-to-b from-zinc-900 to-[#09090b] border border-emerald-500/30 rounded-t-3xl sm:rounded-3xl p-6 relative shadow-[0_-12px_60px_rgba(16,185,129,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400"
          aria-label="Close"
          data-testid="promo-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] font-black tracking-[0.2em] uppercase text-emerald-400">SmartKick</div>
            <div className="text-base font-black leading-tight">Predictions</div>
          </div>
          <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-black uppercase tracking-wider">
            <Crown className="w-3 h-3" /> 14-day free
          </span>
        </div>

        <h2 className="text-2xl font-black leading-tight mb-2">
          Want to know who's going to <span className="text-emerald-400">win</span>?
        </h2>
        <p className="text-sm text-zinc-400 mb-5 leading-relaxed">
          Our AI analyses thousands of data points per match to deliver predictions with confidence scores. Try VIP <b className="text-white">free for 14 days</b> — cancel anytime before day 15.
        </p>

        <ul className="space-y-2 mb-6">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-[13px] text-zinc-300">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              {f}
            </li>
          ))}
        </ul>

        <TrialTag
          {...trialAttrs}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-emerald-500 hover:bg-emerald-600 rounded-full text-white text-sm font-extrabold shadow-[0_0_24px_rgba(16,185,129,0.55)] transition-colors"
          data-testid="promo-cta-trial"
        >
          {native ? 'Unlock AI Predictions' : 'Start 14-day free trial'}
          <ArrowRight className="w-4 h-4" />
        </TrialTag>
        <PreviewTag
          {...previewAttrs}
          className="w-full mt-2 flex items-center justify-center gap-1.5 px-5 py-3 text-[13px] font-bold text-zinc-300 hover:text-white"
          data-testid="promo-cta-preview"
        >
          <Target className="w-3.5 h-3.5" />
          See today's free preview first
        </PreviewTag>

        <p className="text-center text-[10px] text-zinc-500 mt-3">
          Cancel any time · 18+ · BeGambleAware.org
        </p>
      </div>
    </div>
  );
};

export default PredictionsPromoModal;
